import type { Article } from "../articles";
import { PRICING_2026 } from "../guides-data";

const p = PRICING_2026.hvac;

export const articleSignsAcDying: Article = {
  slug: "signs-your-ac-is-dying-repair-vs-replace",
  title: "Is Your AC Failing or Just Having a Bad Day? How to Tell the Difference",
  description: "Frozen coils, short-cycling, and a bill that jumped 30%: some of these are cheap fixes, others mean the system is done. Here's how to read the symptoms before calling anyone.",
  category: "HVAC",
  tags: ["HVAC", "Air Conditioning", "Home Maintenance", "DIY Tips"],
  createdAt: "June 19, 2026",
  updatedAt: "July 1, 2026",
  lastVerified: "July 1, 2026",
  readTime: "8 min read",
  keyTakeaway: "Most AC problems fall into one of two categories: symptoms with a simple fix (dirty filter, tripped breaker, clogged drain line) and symptoms that signal the system is near the end of its service life (compressor failure, refrigerant loss on an aging unit, short-cycling with no clean cause). Age is the most reliable factor, systems over 12 years old with a repair quote above $1,000 are almost always better candidates for replacement.",
  author: { name: "Mauricio L.", url: "/authors/mauricio-l/" },
  sources: [
    {
      name: "Air Conditioning Contractors of America (ACCA) - Residential HVAC Repair Standards",
      url: "https://www.acca.org"
    },
    {
      name: "U.S. EPA - Phaseout of HCFC Refrigerants (R-22)",
      url: "https://www.epa.gov/ods-phaseout"
    }
  ],
  keyMetrics: [
    { label: "Central AC Lifespan", value: `${p.lifespanYears.centralAc.min}-${p.lifespanYears.centralAc.max} Years` },
    { label: "R-22 Phaseout", value: "Fully Phased Out (EPA)" },
    { label: "Repair Threshold Rule", value: "Age × Cost > $5,000" },
    { label: "Compressor Replacement", value: "$1,500 - $3,000" }
  ],
  faqs: [
    {
      question: "How much does it cost to replace a failed AC compressor?",
      answer: "Compressor replacement typically runs $1,500 to $3,000 including refrigerant recharge and labor, though costs vary by unit size and local rates. On a system under warranty, that repair can make sense. On a system over 10 years old, it almost never does: you're spending a significant amount to extend the life of components (coil, blower motor, electrical contacts) that are also approaching end of life."
    },
    {
      question: "Is it worth replacing a compressor on a 10-year-old AC?",
      answer: "Rarely. A compressor failure at 10 years signals the system is in its final phase of service. Replacing just the compressor doesn't address the blower motor, capacitors, or coil, any of which can fail within the next few seasons. The repair cost is better applied toward a new system."
    }
  ],
  content: `
    <p>
      Open the door to your indoor air handler closet in August and find the copper refrigerant lines encased in a solid sleeve of ice. The AC is running, the thermostat is set to 70, and the house is still 82 degrees. Freezing in the middle of summer sounds impossible, but it's one of the more common signs a system is struggling.
    </p>
    <p>
      Some AC symptoms are cheap to fix. Others mean the equipment is done. The problem is they can look identical from the outside. This article breaks down which is which so you know whether to call a technician for a $150 service visit or start pricing replacements.
    </p>

    <div class="my-6 p-4 rounded-lg bg-surface border border-border-custom">
      <p class="font-bold text-foreground m-0 mb-2">The Short Version</p>
      <p class="text-sm text-text-muted m-0">
        Frozen coils, weak airflow, and strange sounds are all worth investigating before assuming the system is dead: many resolve with a filter change, a refrigerant top-off, or a new capacitor. The symptoms that actually indicate end of life are compressor failure, refrigerant loss on a unit over 10 years old, and persistent short-cycling with no identifiable cause. If your system is over 12 years old and the repair quote exceeds $1,000, run the $5,000 rule before writing the check.
      </p>
    </div>

    <h2>What this article covers:</h2>
    <ul>
      <li>Five symptoms and what they actually mean, including the ones with simple fixes</li>
      <li>Why frozen coils happen and the two-step test to diagnose the cause</li>
      <li>How to use the $5,000 rule to make the repair-or-replace call</li>
      <li>The R-22 refrigerant situation and why it changes the math on older units</li>
    </ul>

    <h2>Frozen Coils: Cheap Fix or Refrigerant Problem?</h2>
    <p>
      When the evaporator coil freezes, it shuts down all cooling, ice blocks the airflow and the system runs without moving cold air. It looks alarming, but the cause is usually one of two things, and one of them costs nothing to fix.
    </p>
    <p>
      <strong>Restricted airflow</strong> is the most common cause. A severely clogged filter prevents enough warm room air from flowing across the coil, dropping its surface temperature below freezing. Fix: turn the system off, let it thaw for 24 hours with the fan running on "On," replace the filter, and restart. If it doesn't refreeze, you're done.
    </p>
    <p>
      <strong>Low refrigerant charge</strong> is the other cause, and it's more serious. Without sufficient refrigerant pressure, the coil runs too cold. If ice returns within a few hours of restarting after a thaw, you have a leak that needs professional diagnosis. On a system under 8 years old, a leak repair and recharge is reasonable. On one that's 12 or older, the refrigerant situation compounds with the age question below.
    </p>

    <h2>What the Sounds Are Telling You</h2>
    <p>
      A well-functioning AC is a low hum. Anything else is diagnostic information.
    </p>
    <p>
      A <strong>high-pitched screech</strong> from the air handler typically means the blower motor bearings are failing. This is a repair, blower motors run a few hundred dollars, but it needs to happen before the motor seizes and takes the electrical contacts with it.
    </p>
    <p>
      <strong>Clanking or banging</strong> from the outdoor unit points to a loose fan blade or, more seriously, broken internal compressor valves. The compressor is the most expensive component in the system. If the clanking is coming from inside the compressor housing, get a replacement quote alongside the repair estimate before deciding.
    </p>
    <p>
      A <strong>gurgling or bubbling</strong> sound in the refrigerant lines is air in the system, which means refrigerant has leaked out and air has gotten in. This always requires professional service.
    </p>

    <!--COMPONENT:pullquote-->If your system runs for hours but the indoor temperature barely drops, the compressor has lost compression capacity. It's consuming electricity but not moving heat. That is not a maintenance problem: it's a failure.<!--ENDOFCOMPONENT-->

    <h2>Short-Cycling: Hard on Equipment, Easy to Misread</h2>
    <p>
      A system that runs for two or three minutes, shuts off, and immediately restarts is short-cycling. It's worth diagnosing before assuming the worst: the cause is sometimes simple.
    </p>
    <p>
      An oversized system short-cycles by design: it cools the air near the thermostat too quickly, satisfies the call for cooling, and shuts off before the rest of the house equilibrates. A dirty condenser coil causes the system to overheat and trip its high-pressure safety limit. A failing thermostat can send erratic signals.
    </p>
    <p>
      A technician can identify the root cause in under an hour. If it's a thermostat or a coil cleaning, the fix is inexpensive. If short-cycling persists after those are ruled out, the compressor or its electrical components are the likely culprit.
    </p>

    <div class="my-8 p-6 rounded-xl border border-dashed border-emerald-500/30 bg-emerald-500/5 flex flex-col sm:flex-row items-center gap-6 justify-between shadow-card">
      <div class="flex-1">
        <h3 class="text-xl font-bold text-foreground font-display m-0 mb-2">Estimate Your AC Replacement Cost</h3>
        <p class="text-sm text-text-muted m-0">Use our free HVAC Cost Calculator to get installed price ranges for a new unit based on your home's square footage and system type.</p>
      </div>
      <a href="/calculators/hvac/" class="inline-flex items-center justify-center px-6 py-3 rounded-lg bg-[var(--accent)] text-white hover:bg-[var(--accent-dark)] font-mono font-bold text-xs uppercase tracking-wider transition-colors shrink-0 text-center whitespace-nowrap w-full sm:w-auto min-w-[180px]">Go to HVAC Calculator</a>
    </div>

    <h2>The R-22 Refrigerant Problem</h2>
    <p>
      If your system was manufactured before 2010, it likely uses R-22 (Freon) refrigerant. The U.S. EPA completed its phaseout of R-22 production in 2020 under the Clean Air Act. Any R-22 still in service comes from pre-phaseout stockpiles, and the price has risen sharply as supplies tighten.
    </p>
    <p>
      A refrigerant recharge on an R-22 system can cost several hundred dollars more than the same service on a modern R-410A or R-32 system, just for the refrigerant itself. If you have an R-22 unit that's leaking, factor that cost into the repair-or-replace calculation (for more details on modern efficiency standards, read our <a href="/articles/seer-rating-explained-ac-bill/" class="text-[var(--accent)] hover:underline font-semibold">SEER2 rating guide</a> or our <a href="/articles/hvac-replacement-cost-guide/" class="text-[var(--accent)] hover:underline font-semibold">HVAC replacement cost guide</a>). The system can't be converted to a newer refrigerant type; retrofits are not compatible.
    </p>

    <h2>The $5,000 Rule: How to Make the Call</h2>
    <p>
      Multiply the system's age (in years) by the repair quote (in dollars). If the result exceeds $5,000, the repair rarely makes financial sense.
    </p>
    <p>
      Example: a $600 repair on a 9-year-old system produces $5,400. That's right at the threshold, worth pausing and asking a second contractor for their read on the system's overall condition before committing.
    </p>
    <p>
      A $400 capacitor swap on a 6-year-old system produces $2,400. Do the repair. A $2,000 compressor replacement on a 13-year-old system produces $26,000. Don't.
    </p>
    <p>
      The rule is a starting point, not a guarantee, but it filters out the obvious decisions and forces you to think about the money going forward, not just the money today. Use our <a href="/calculators/hvac/" class="text-accent underline decoration-accent/30 hover:decoration-accent/60 transition-colors">HVAC Cost Calculator</a> to see what a replacement would run for your home's square footage.
    </p>
  `
};